<template>
  <article
    class="support-page"
    :class="{ 'support-page--dark': $vuetify.theme.dark }"
    lang="en"
    aria-labelledby="support-title"
  >
    <div class="support-layout">
      <aside class="support-index">
        <details :open="$vuetify.breakpoint.mdAndUp">
          <summary>{{ $t("On this page") }}</summary>
          <nav :aria-label="$t(&quot;Support sections&quot;)">
            <a href="#how-can-we-help">{{ $t("How can we help?") }}</a>
            <a href="#contact-support">{{ $t("Contact support") }}</a>
            <a href="#make-it-better">{{ $t("Help us improve") }}</a>
          </nav>
        </details>
      </aside>
      <div class="support-body">
        <header>
          <p class="support-eyebrow">
            {{ $t("\n            Self Biography Support\n          ") }}
          </p>
          <h1 id="support-title">
            {{ $t("\n            We're here to help.\n          ") }}
          </h1>
          <p>{{ $t("Self Biography is designed to be simple, but sometimes you need an answer, something doesn't work as expected, or you simply need a little help figuring out what to do next.") }}</p>
          <p>{{ $t("Start here and we'll help point you in the right direction.") }}</p>
        </header>
        <section
          class="support-section"
          aria-labelledby="how-can-we-help"
        >
          <h2 id="how-can-we-help">
            {{ $t("\n            How can we help?\n          ") }}
          </h2>
          <div class="support-help-grid">
            <div class="support-help">
              <h3>{{ $t("I need help using Self Biography") }}</h3>
              <p>
                {{ $t("Visit our ") }}<router-link to="/pages/documentation">
                  {{ $t("Documentation") }}
                </router-link>{{ $t(" for step-by-step instructions on Memory Bits, importing photographs, Photo Scan, privacy controls, Personal and Professional Biographies, AI tools, publishing and more.") }}
              </p>
            </div>
            <div class="support-help">
              <h3>{{ $t("I have a question") }}</h3>
              <p>
                {{ $t("\n                Check our ") }}<router-link to="/pages/faq">
                  {{ $t("\n                  FAQ\n                ") }}
                </router-link>{{ $t(" for answers to the most common questions about Self Biography, privacy, AI, sharing, publishing and preserving your life.\n              ") }}
              </p>
            </div>
            <div class="support-help">
              <h3>{{ $t("Something isn't working") }}</h3>
              <p>
                {{ $t("\n                Found a bug? Is a feature not behaving correctly? ") }}<a
                  href="#contact-support"
                  @click="selectTopic('Technical Problem')"
                >{{ $t("Tell us what happened") }}</a>{{ $t(", what device you're using, and what you were trying to do. Screenshots are always helpful.\n              ") }}
              </p>
            </div>
            <div class="support-help">
              <h3>{{ $t("I can't access my account") }}</h3>
              <p>
                {{ $t("\n                If you've forgotten your password, lost access to your account, or believe someone else may be trying to access it, use ") }}<router-link to="/auth">
                  {{ $t("\n                  Account Recovery\n                ") }}
                </router-link>{{ $t(" or ") }}<a
                  href="#contact-support"
                  @click="selectTopic('Account')"
                >{{ $t("contact us immediately") }}</a>.
              </p>
            </div>
            <div class="support-help">
              <h3>{{ $t("Privacy or security concern") }}</h3>
              <p>
                {{ $t("\n                If you believe your account or personal information may have been compromised, or something appears that you did not authorize, ") }}<a
                  href="#contact-support"
                  @click="selectTopic('Privacy & Security')"
                >{{ $t("let us know immediately") }}</a>{{ $t(". Security and privacy issues receive priority attention.\n              ") }}
              </p>
            </div>
            <div class="support-help">
              <h3>{{ $t("Billing or subscription help") }}</h3>
              <p>
                {{ $t("\n                Questions about your plan, charges, subscription, AI credits, or account? Contact ") }}<a
                  href="#contact-support"
                  @click="selectTopic('Billing')"
                >{{ $t("Billing Support") }}</a>.
              </p>
            </div>
            <div class="support-help support-help--wide">
              <h3>{{ $t("Affiliate support") }}</h3>
              <p>
                {{ $t("\n                For questions about referrals, affiliate commissions, tracking, or your affiliate account, visit ") }}<a
                  href="#contact-support"
                  @click="selectTopic('Affiliate')"
                >{{ $t("Affiliate Support") }}</a>.
              </p>
            </div>
            <div class="support-help support-help--wide">
              <h3>{{ $t("I have an idea") }}</h3>
              <p>
                <a
                  href="#contact-support"
                  @click="selectTopic('Feature Request')"
                >{{ $t("Please send it.") }}</a>
              </p>
              <p>{{ $t("Self Biography itself has grown through ideas, experimentation and people telling us what would make it better. If there's something you think we should add, change, simplify or improve, ") }}<strong>{{ $t("we want to hear it.") }}</strong></p>
            </div>
          </div>
        </section>
        <section
          class="support-section support-contact"
          aria-labelledby="contact-support"
        >
          <h2 id="contact-support">
            {{ $t("\n            Contact support\n          ") }}
          </h2>
          <fieldset>
            <legend>{{ $t("Tell us what you need help with:") }}</legend>
            <div class="support-topics">
              <label
                v-for="topic in topics"
                :key="topic"
                class="support-topic"
                :class="{ 'support-topic--selected': selectedTopic === topic }"
              >
                <input
                  v-model="selectedTopic"
                  type="radio"
                  name="support-topic"
                  :value="topic"
                />
                <span>{{ topic }}</span>
              </label>
            </div>
          </fieldset>
          <v-btn
            color="primary"
            large
            @click="dialog = true"
          >
            {{ $t("\n            Contact support\n          ") }}
          </v-btn>
          <span class="support-email">{{ supportEmail }}</span>
          <p>{{ $t("When contacting us, please include the email associated with your account and, when relevant, the device and browser you are using.") }}</p>
          <p class="support-password-note">
            {{ $t("\n            Please never send us your password.\n          ") }}
          </p>
        </section>
        <section
          class="support-section"
          aria-labelledby="make-it-better"
        >
          <h2 id="make-it-better">
            {{ $t("\n            Help us make Self Biography better\n          ") }}
          </h2>
          <p>{{ $t("Self Biography is continuously evolving.") }}</p>
          <p>{{ $t("If something is confusing, that's useful information for us too. Sometimes the problem isn't that a user doesn't understand the software—") }}<strong>{{ $t("sometimes the software needs to be easier to understand.") }}</strong></p>
          <p>{{ $t("So whether you've discovered a bug, have an idea for a feature, or simply think ") }}<em>{{ $t("“there has to be an easier way to do this,”") }}</em>{{ $t(" tell us.") }}</p>
        </section>
      </div>
    </div>
    <support-dialog
      v-model="dialog"
      :topic="selectedTopic"
    />
  </article>
</template>

<script>
import { t } from '@/i18n'

import SupportDialog from '@/components/SupportDialog.vue'

export default {
  name: 'SelfBiographySupport',
  components: { SupportDialog },
  data: () => ({
    supportEmail: 'support@selfbiography.com',
    dialog: false,
    selectedTopic: 'Something Else',
    topics: ['Account', 'Technical Problem', 'Privacy & Security', 'Billing', 'Affiliate', 'Feature Request', 'Something Else'],
    previousTitle: '',
  }),
  mounted() {
    this.previousTitle = document.title
    document.title = t('Support | Self Biography')
    if (this.topics.includes(this.$route.query.topic)) this.selectedTopic = this.$route.query.topic
    this.$nextTick(() => {
      const id = this.$route.hash.slice(1)
      const target = ['how-can-we-help', 'contact-support', 'make-it-better'].includes(id) && document.getElementById(id)
      if (target) target.scrollIntoView()
      else window.scrollTo(0, 0)
    })
  },
  beforeDestroy() {
    document.title = this.previousTitle
  },
  methods: {
    selectTopic(topic) {
      this.selectedTopic = topic
      this.dialog = true
    },
  },
}
</script>

<style lang="scss" scoped>
.support-page {
  --support-text: #5e5669;
  --support-heading: #393345;
  --support-accent: #713ac7;
  --support-surface: #fff;
  --support-tint: #f3eefb;
  --support-divider: rgba(94, 86, 105, 0.16);
  max-width: 1160px;
  margin: 32px auto 48px;
  color: var(--support-text);
  font-size: 16px;
  line-height: 1.8;
  text-align: left;
  &--dark {
    --support-text: #d0cadf;
    --support-heading: #eee9fa;
    --support-accent: #c6a5ff;
    --support-surface: #312d4b;
    --support-tint: #39304f;
    --support-divider: rgba(231, 227, 252, 0.16);
  }
  h1, h2 { color: var(--support-heading); font-weight: 600; line-height: 1.3; text-align: left; }
  h1 { font-size: 16px; letter-spacing: -0.035em; margin-bottom: 24px; }
  h2 { font-size: 16px; letter-spacing: -0.02em; margin-bottom: 18px; scroll-margin-top: 88px; }
  p { margin-bottom: 18px; }
  a { color: var(--support-accent); text-underline-offset: 4px; }
  a:focus-visible, summary:focus-visible { outline: 2px solid var(--support-accent); outline-offset: 4px; }
  .support-eyebrow { color: var(--support-accent); font-size: 0.75rem; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 14px; }
}
.support-layout { display: grid; grid-template-columns: 200px minmax(0, 1fr); gap: 32px; align-items: start; }
.support-index { position: sticky; top: 88px; }
.support-index summary { font-weight: 600; color: var(--support-heading); cursor: pointer; min-height: 44px; }
.support-index nav { display: flex; flex-direction: column; border-left: 1px solid var(--support-divider); padding-left: 16px; }
.support-index a { display: flex; align-items: center; min-height: 44px; padding: 6px 0; font-size: 0.875rem; text-decoration: none; line-height: 1.5; }
.support-index a:hover { text-decoration: underline; }
.support-body { min-width: 0; background: var(--support-surface); border: 1px solid var(--support-divider); border-radius: 8px; padding: 44px; }
.support-section { margin-top: 36px; padding-top: 32px; border-top: 1px solid var(--support-divider); }
@media (max-width: 959px) {
  .support-page { margin-top: 8px; margin-bottom: 32px; }
  .support-layout { grid-template-columns: 1fr; gap: 20px; }
  .support-index { position: static; }
  .support-index summary { min-height: 44px; padding: 8px 0; }
  .support-index nav { margin-top: 8px; }
  .support-body { padding: 28px; }
}
@media (max-width: 599px) {
  .support-body { padding: 24px 0 0; border: 0; border-top: 1px solid var(--support-divider); border-radius: 0; background: transparent; }
  .support-section { margin-top: 28px; padding-top: 24px; }
}
.support-help-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 24px; }
.support-help { padding: 20px; border: 1px solid var(--support-divider); border-radius: 6px; }
.support-help h3 { color: var(--support-heading); font-size: 16px; font-weight: 600; line-height: 1.5; margin-bottom: 12px; }
.support-help p:last-child { margin-bottom: 0; }
.support-help--wide { grid-column: 1 / -1; }
.support-contact { background: var(--support-tint); padding: 24px; border: 0; border-left: 3px solid var(--support-accent); }
.support-contact fieldset { min-width: 0; border: 0; padding: 0; margin: 0 0 20px; }
.support-contact legend { color: var(--support-heading); font-weight: 500; margin-bottom: 12px; }
.support-topics { display: flex; flex-wrap: wrap; gap: 8px; }
.support-topic { display: flex; align-items: center; gap: 8px; min-height: 44px; padding: 8px 12px; border: 1px solid var(--support-divider); border-radius: 6px; background: var(--support-surface); cursor: pointer; font-size: 0.875rem; line-height: 1.5; }
.support-topic--selected { border-color: var(--support-accent); color: var(--support-accent); }
.support-topic input { width: 16px; height: 16px; flex-shrink: 0; accent-color: var(--support-accent); }
.support-topic:focus-within { outline: 2px solid var(--support-accent); outline-offset: 2px; }
.support-email { display: block; width: fit-content; max-width: 100%; overflow-wrap: anywhere; margin: 12px 0 20px; }
.support-contact .support-password-note { margin-bottom: 0; font-weight: 600; color: var(--support-heading); }
@media (max-width: 599px) {
  .support-help-grid { grid-template-columns: 1fr; gap: 16px; }
  .support-help, .support-contact { padding: 20px; }
  .support-contact .v-btn { width: 100%; }
}
</style>
